<template>
    <tabs value="0">
        <tab-list>
            <tab value="0">X-Axis</tab>
            <tab value="1">Y-Axis</tab>
            <tab v-if="showMultiAxisTab" value="2">Multi-Axis</tab>
        </tab-list>
        <tab-panels>
            <tab-panel value="0">
                <div class="settings-grid centred">
                    <label for="axisValues">Axis Values:</label>
                    <primevue-select-button
                        :model-value="localShowTime ? 'time' : 'period'"
                        :options="axisValueOptions"
                        option-label="name"
                        option-value="id"
                        :allowEmpty="false"
                        input-id="axisValues"
                        @update:model-value="toggleXAxis"
                    />
                    <label for="currentVerticalLine">Current Vertical Line:</label>
                    <primevue-select-button
                        :model-value="localCurrentTimeLine"
                        :options="verticalLineOptions"
                        option-label="name"
                        option-value="id"
                        :allowEmpty="false"
                        input-id="currentVerticalLine"
                        @update:model-value="toggleCurrentLine"
                    />
                    <label for="legendVisibility">Legend:</label>
                    <primevue-select-button
                        :model-value="localShowLegend"
                        :options="legendOptions"
                        option-label="title"
                        option-value="value"
                        :allow-empty="false"
                        input-id="legendVisibility"
                        @update:model-value="toggleLegend"
                    />
                </div>
            </tab-panel>
            <tab-panel value="1">
                <div v-for="yAxis in minMaxValues" :key="yAxis.title">
                    <div v-if="!yAxis.shouldSkip" class="flex-column-with-gap">
                        <h3 class="axis-title">{{ yAxis.title }}</h3>
                        <div class="settings-grid">
                            <label for="customLimits">Limit</label>
                            <div class="flex-column-with-gap">
                                <div class="flex-row-with-gap">
                                    <primevue-checkbox v-model="yAxis.customLimits" color="primary" binary :input-id="`customLimits-${yAxis.title}`" />
                                    <label :for="`customLimits-${yAxis.title}`">Custom Limits</label>
                                    <info-icon-with-tooltip tooltip-text="Set fixed Y Axis limits" />
                                </div>
                                <min-max
                                    :disabled="!yAxis.customLimits"
                                    v-model="yAxis.limits"
                                    allow-negatives
                                    vertical
                                />
                            </div>
                            <label for="customPadding">Extra Space (%)</label>
                            <div class="flex-column-with-gap">
                                <div class="flex-row-with-gap">
                                    <primevue-checkbox v-model="yAxis.customPadding" color="primary" binary :input-id="`customPadding-${yAxis.title}`" />
                                    <label :for="`customPadding-${yAxis.title}`">Custom Padding</label>
                                    <info-icon-with-tooltip tooltip-text="Set extra space above max/below min as a % of total chart height" />
                                </div>
                                <min-max
                                    :disabled="!yAxis.customPadding"
                                    v-model="yAxis.padding"
                                    vertical
                                />
                            </div>
                            <label for="dynamicExpand">Auto-contract</label>
                            <div class="flex-column-with-gap">
                                <div class="flex-row-with-gap">
                                    <primevue-checkbox v-model="yAxis.dynamicExpand" color="primary" binary :input-id="`dynamicExpand-${yAxis.title}`" />
                                    <label :for="`dynamicExpand-${yAxis.title}`">Auto-contract to fit remaining data</label>
                                    <info-icon-with-tooltip tooltip-text="When enabled, the chart can re-calculate axis min/max from available data (optionally within your custom limits)." />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </tab-panel>
            <tab-panel v-if="showMultiAxisTab" value="2">
                <div class="flex-column-with-gap multi-axis-sections">
                    <div>
                        <label>This chart has series with different units</label>
                    </div>
                    <div class="flex-column-with-gap">
                        <label>Use this option to separate them onto different charts</label>
                        <primevue-select-button
                            :model-value="localAxisStackType"
                            :options="axisStackOptions"
                            option-label="name"
                            option-value="id"
                            :allow-empty="false"
                            @update:model-value="updateAxisStackType"
                        >
                            <template #option="slotProps">
                                <div class="select-button-inner">
                                    {{ slotProps.option.name }}
                                    <info-icon-with-tooltip
                                        v-if="slotProps.option.info"
                                        :tooltip-text="slotProps.option.info"
                                    />
                                </div>
                            </template>
                        </primevue-select-button>
                        <div v-if="localAxisStackType === 'stack'" class="inputs-holder">
                            <div class="axis-card" v-for="(axisPosition, index) in localAxisPositions" :key="axisPosition.label">
                                <label>{{ axisPosition.label }}</label>
                                <primevue-input-number
                                    :model-value="axisPosition.value"
                                    @update:model-value="($event: number | null) => updateAxisPosition($event, index)"
                                    :min="0"
                                    :max="100"
                                    :step="1"
                                    show-buttons
                                />
                            </div>
                        </div>
                    </div>
                    <div class="flex-column-with-gap">
                        <label>Axis label positioning</label>
                        <primevue-select-button
                            :model-value="localAxisSide"
                            :options="axisSideOptions"
                            option-label="name"
                            option-value="id"
                            :allow-empty="false"
                            @update:model-value="updateAxisSide"
                        >
                            <template #option="slotProps">
                                <div class="select-button-inner">
                                    {{ slotProps.option.name }}
                                    <info-icon-with-tooltip
                                        v-if="slotProps.option.info"
                                        :tooltip-text="slotProps.option.info"
                                    />
                                </div>
                            </template>
                        </primevue-select-button>
                        <div v-if="localAxisSide === 'manual'" class="inputs-holder">
                            <div class="axis-card" v-for="(axis, index) in localAxisLeftOrRight" :key="axis.label">
                                <span class="axis-title">{{ axis.label }}</span>
                                <primevue-select-button
                                    :model-value="axis.value"
                                    @update:model-value="($event: 'left' | 'right') => updateAxisLeftOrRight($event, index)"
                                    :options="axisPositionOptions"
                                    :allow-empty="false"
                                    option-label="name"
                                    option-value="id"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </tab-panel>
        </tab-panels>
    </tabs>
</template>

<script lang="ts" setup>
import { IAxisLimits } from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import { IChartMultipleAxisOptions, IChartMultipleAxisPosition, IChartMultipleAxisSide } from '@/interfaces/powerMarkets/charts/IChartMultipleAxisOptions.ts';
import { IMinMax } from '@/interfaces/general/IMinMax.ts';
import { ref, computed, watch } from 'vue';
import PrimevueCheckbox from 'primevue/checkbox';
import PrimevueInputNumber from 'primevue/inputnumber';
import PrimevueSelectButton from 'primevue/selectbutton';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import MinMax from '@/components/powerMarkets/input/MinMax.vue';
import InfoIconWithTooltip from '@/components/common/InfoIconWithTooltip.vue';
import { IFlagOption } from '@/interfaces/general/IFlagOption.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';

interface IProcessedAxis extends Omit<IAxisLimits, 'title' | 'min' | 'max' | 'minPadding' | 'maxPadding'> {
    title: string;
    limits: IMinMax;
    padding: IMinMax;
    customLimits: boolean;
    customPadding: boolean;
    dynamicExpand: boolean;
    shouldSkip?: boolean;
}

const props = defineProps<{
    yAxisArray: IAxisLimits[];
    showTime: boolean;
    currentTimeLine?: string;
    showLegend?: boolean;
    multipleAxisOptions?: IChartMultipleAxisOptions | null;
}>();

const emit = defineEmits<{
    'toggle-x-axis': [];
    'toggle-current-line': [string];
    'toggle-legend': [boolean];
    'y-axis-limits': [Array<IAxisLimits>];
    'multiple-axis-options': [IChartMultipleAxisOptions];
    'can-save-axis-changes': [boolean];
}>();

const minMaxValues = ref<IProcessedAxis[]>([]);

const axisValueOptions: Array<IIdName> = [
    { name: 'Time', id: 'time' },
    { name: 'Period', id: 'period' },
];

const verticalLineOptions: Array<IIdName> = [
    { name: 'Period', id: 'period' },
    { name: '1 Min', id: '1min' },
    { name: '5 Min', id: '5min' },
];

const legendOptions: Array<IFlagOption> = [
    { title: 'Show', value: true },
    { title: 'Hide', value: false },
];

const axisStackOptions: Array<IIdName> = [
    { id: 'overlap', name: 'Same Chart', info: 'Series of different units appear on the same figure.' },
    { id: 'stack', name: 'Different Charts ( % )', info: 'Series of different units appear in separate figures.' },
];

const axisSideOptions: Array<IIdName> = [
    { id: 'same', name: 'Left', info: 'Y axes appear on the same side (left)' },
    { id: 'automatic', name: 'Automatic', info: 'Y axes will appear on alternating opposite sides' },
    { id: 'manual', name: 'Manual', info: 'Pick which side each Y-axis will appear on' },
];

const axisPositionOptions: Array<IIdName> = [
    { id: 'left', name: 'Left' },
    { id: 'right', name: 'Right' },
];

const localShowTime = ref<boolean>(props.showTime);
const localCurrentTimeLine = ref<string | null>(props.currentTimeLine ?? null);
const localShowLegend = ref<boolean>(props.showLegend ?? true);
const localAxisStackType = ref<string>(props.multipleAxisOptions?.axisStackType ?? 'overlap');
const localAxisSide = ref<string>(props.multipleAxisOptions?.axisSide ?? 'same');
const localAxisPositions = ref<Array<IChartMultipleAxisPosition> | null>(cloneAxisPositions(props.multipleAxisOptions?.axisPositions ?? null));
const localAxisLeftOrRight = ref<Array<IChartMultipleAxisSide>>(cloneAxisLeftOrRight(props.multipleAxisOptions?.axisLeftOrRight ?? []));

const showMultiAxisTab = computed<boolean>(() => !!props.multipleAxisOptions);

const canSave = computed<boolean>(() => minMaxValues.value.every(m => {
    if (!m.customLimits) return true;

    const hasValidMin = m.limits.min === null || !Number.isNaN(Number(m.limits.min));
    const hasValidMax = m.limits.max === null || !Number.isNaN(Number(m.limits.max));

    if (!hasValidMin || !hasValidMax) return false;

    const parsedMin = m.limits.min === null ? null : Number(m.limits.min);
    const parsedMax = m.limits.max === null ? null : Number(m.limits.max);
    if (parsedMin != null && parsedMax != null && parsedMin > parsedMax) return false;

    return true;
}) && canSaveMultipleAxisChanges.value);

const canSaveMultipleAxisChanges = computed<boolean>(() => {
    if (!showMultiAxisTab.value) return true;
    if (localAxisStackType.value !== 'stack') return true;
    if (!localAxisPositions.value || localAxisPositions.value.length === 0) return false;

    const totalPositions = localAxisPositions.value.reduce((accumulator, axisPosition) => accumulator + axisPosition.value, 0);

    return Math.abs(100 - totalPositions) <= 1;
});

const formattedYAxisUpdates = computed<Array<IAxisLimits>>(() => minMaxValues.value.map(yAxis => ({
    userMin: yAxis.customLimits && yAxis.limits.min !== null ? parseFloat(String(yAxis.limits.min)) : null,
    userMax: yAxis.customLimits && yAxis.limits.max !== null ? parseFloat(String(yAxis.limits.max)) : null,
    min: yAxis.customLimits && yAxis.limits.min !== null ? parseFloat(String(yAxis.limits.min)) : null,
    max: yAxis.customLimits && yAxis.limits.max !== null ? parseFloat(String(yAxis.limits.max)) : null,
    minPadding: yAxis.customPadding && yAxis.padding.min !== null ? (Number(yAxis.padding.min) / 100) : null,
    maxPadding: yAxis.customPadding && yAxis.padding.max !== null ? (Number(yAxis.padding.max) / 100) : null,
    dynamicExpand: yAxis.dynamicExpand,
    title: { text: yAxis.title },
})));

const formattedMultipleAxisOptions = computed<IChartMultipleAxisOptions | null>(() => {
    if (!showMultiAxisTab.value) return null;

    return {
        axisStackType: localAxisStackType.value,
        axisSide: localAxisSide.value,
        axisPositions: cloneAxisPositions(localAxisPositions.value),
        axisLeftOrRight: cloneAxisLeftOrRight(localAxisLeftOrRight.value),
    };
});

function cloneAxisPositions (axisPositions: Array<IChartMultipleAxisPosition> | null): Array<IChartMultipleAxisPosition> | null {
    if (!axisPositions) return null;

    return axisPositions.map(axisPosition => ({ ...axisPosition }));
}

function cloneAxisLeftOrRight (axisLeftOrRight: Array<IChartMultipleAxisSide>):Array<IChartMultipleAxisSide> {
    return axisLeftOrRight.map(axisPosition => ({ ...axisPosition }));
}

function emitMultipleAxisOptions (): void {
    const options = formattedMultipleAxisOptions.value;
    if (!options) return;
    emit('multiple-axis-options', options);
}

function buildDefaultAxisPositions (): Array<IChartMultipleAxisPosition> | null {
    const axisLabels = localAxisLeftOrRight.value.length
        ? localAxisLeftOrRight.value.map(axis => axis.label)
        : minMaxValues.value.filter(axis => !axis.shouldSkip).map(axis => axis.title);

    if (axisLabels.length <= 1) return null;

    const defaultPercentage = Math.round(100 / axisLabels.length);

    return axisLabels.map(label => ({
        label,
        value: defaultPercentage,
        edited: false,
    }));
}

function buildAxisLeftOrRightOptions (): void {
    localAxisLeftOrRight.value = minMaxValues.value
        .filter(axis => !axis.shouldSkip)
        .map(axis => {
            const existingAxis = localAxisLeftOrRight.value.find(entry => entry.label === axis.title);

            return existingAxis ? { ...existingAxis } : {
                label: axis.title,
                value: 'left',
            };
        });
}

function toggleXAxis (option: string): void {
    if (option === (localShowTime.value ? 'time' : 'period')) return;
    localShowTime.value = !localShowTime.value;
    emit('toggle-x-axis');
}

function toggleCurrentLine (option: string): void {
    if (option === localCurrentTimeLine.value) return;
    localCurrentTimeLine.value = option;
    emit('toggle-current-line', option);
}

function toggleLegend (option: boolean): void {
    if (option === localShowLegend.value) return;
    localShowLegend.value = option;
    emit('toggle-legend', option);
}

function updateAxisStackType (value: string): void {
    if (value === localAxisStackType.value) return;
    localAxisStackType.value = value;

    if (value === 'stack' && (!localAxisPositions.value || localAxisPositions.value.length === 0)) {
        localAxisPositions.value = buildDefaultAxisPositions();
    }

    emitMultipleAxisOptions();
}

function updateAxisSide (value: string): void {
    if (value === localAxisSide.value) return;
    localAxisSide.value = value;

    if (value === 'manual') {
        buildAxisLeftOrRightOptions();
    }

    emitMultipleAxisOptions();
}

function updateAxisPosition (value: number | null, index: number): void {
    if (!localAxisPositions.value || value == null) return;
    if (localAxisPositions.value[index].value === value) return;
    localAxisPositions.value[index].value = value;

    emitMultipleAxisOptions();
}

function updateAxisLeftOrRight (value: 'left' | 'right', index: number): void {
    if (localAxisLeftOrRight.value[index].value === value) return;
    localAxisLeftOrRight.value[index].value = value;

    emitMultipleAxisOptions();
}

function initialise (): void {
    props.yAxisArray.forEach((axis) => {
        const titleText = Array.isArray(axis.title.text) ? axis.title.text[0] : axis.title.text;

        const userMin = axis.userMin !== undefined ? axis.userMin : axis.min;
        const userMax = axis.userMax !== undefined ? axis.userMax : axis.max;

        const formatted: IProcessedAxis = {
            limits: {
                min: userMin ?? null,
                max: userMax ?? null,
            },
            padding: {
                min: (axis.minPadding ?? 0) * 100,
                max: (axis.maxPadding ?? 0) * 100,
            },
            customLimits: userMin != null || userMax != null,
            customPadding: axis.minPadding != null || axis.maxPadding != null,
            title: titleText,
            dynamicExpand: axis.dynamicExpand ?? false,
        };
        minMaxValues.value.push(formatted);
    });

    if (
        props.yAxisArray.length === 2
    && props.yAxisArray[0].title.text === ''
    ) {
        minMaxValues.value[1].shouldSkip = true;
        minMaxValues.value[0].title = Array.isArray(props.yAxisArray[1].title.text)
            ? props.yAxisArray[1].title.text[0]
            : props.yAxisArray[1].title.text;
    }

    if (localAxisSide.value === 'manual') {
        buildAxisLeftOrRightOptions();
    }
}

initialise();

watch(formattedYAxisUpdates, () => {
    emit('y-axis-limits', formattedYAxisUpdates.value);
}, { deep: true });

watch(canSave, () => {
    emit('can-save-axis-changes', canSave.value);
}, { immediate: true });

</script>

<style lang="scss" scoped>

.settings-grid {
    align-items: flex-start;
    display: grid;
    gap: var(--largeSpacing);
    grid-template-columns: auto 1fr;

    &.centred {
        align-items: center;
    }
}

.axis-title {
    font-weight: var(--boldFontWeight);
}

.inputs-holder {
    display: flex;
    gap: var(--defaultSpacing);
    flex-wrap: wrap;
}

.axis-card {
    display: flex;
    align-items: center;
    gap: var(--largeSpacing);

    :deep(.p-inputnumber-input) {
        width: 72px;
    }
}

.select-button-inner {
    display: flex;
    align-items: center;
    gap: var(--smallSpacing);
}

.multi-axis-sections {
    gap: 3rem;
}

</style>
